// src/pages/Admin/Settings/TeamManagement.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getAllTeamMembers,
  deleteTeamMember,
  updateTeamOrder,
  updateTeamMemberPhoto,
} from "../../services/teamServices";

import Button from "../../components/Button/Button";
import ButtonEdit from "../../components/Button/ButtonEdit";
import ButtonDelete from "../../components/Button/ButtonDelete";

import {
  FaPlus,
  FaSave,
  FaTimes,
  FaSearch,
  FaUsers,
  FaLayerGroup,
  FaImage,
  FaUndo,
  FaCamera,
} from "react-icons/fa";
import {
  TbTriangleFilled,
  TbTriangleInvertedFilled,
} from "react-icons/tb";
import { ToastContainer, toast } from "react-toastify";

import CreateTeamMemberModal from "./CreateTeamMemberModal";
import EditTeamMemberModal from "./EditTeamMemberModal";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function stripHtml(html) {
  if (!html) return "";
  return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function truncate(text, max = 90) {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function normalizeMembers(arr = []) {
  return [...arr]
    .map((m, index) => ({
      ...m,
      position:
        typeof m.position === "number" && !Number.isNaN(m.position)
          ? m.position
          : index + 1,
    }))
    .sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999))
    .map((m, index) => ({
      ...m,
      position: index + 1,
    }));
}

const TeamManagement = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isReordering, setIsReordering] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const defaultAvatar = "/img/user_avatar.jpg";

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    ) {
      return s;
    }
    if (s.startsWith("/uploads/")) {
      const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
      return `${base}${s}`;
    }
    return s;
  };

  const fetchTeamMembers = async () => {
    setErrSafe("");
    setLoading(true);
    try {
      const data = await getAllTeamMembers();
      setTeam(normalizeMembers(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error("Erreur chargement team:", error);
      toast.error("Impossible de charger l’équipe.");
      setTeam([]);
    } finally {
      setLoading(false);
    }
  };

  // juste pour éviter un warning inutile si tu veux enrichir plus tard
  const [, setErrSafe] = useState("");

  useEffect(() => {
    fetchTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedFile) {
      setSelectedPreview("");
      return;
    }

    const url = URL.createObjectURL(selectedFile);
    setSelectedPreview(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [selectedFile]);

  const categories = useMemo(() => {
    return Array.from(
      new Set(
        team
          .map((m) => (m.categorie || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [team]);

  const filteredTeam = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    return team.filter((member) => {
      const matchesCategory =
        categoryFilter === "all" || (member.categorie || "") === categoryFilter;

      if (!matchesCategory) return false;

      if (!q) return true;

      const haystack = [
        member.nom,
        member.prenom,
        member.categorie,
        stripHtml(member.fonction),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [team, searchTerm, categoryFilter]);

  const stats = useMemo(() => {
    const total = team.length;
    const visible = filteredTeam.length;
    const categoriesCount = categories.length;
    const withPhoto = team.filter((m) => !!m.photoURL).length;

    return {
      total,
      visible,
      categoriesCount,
      withPhoto,
    };
  }, [team, filteredTeam, categories]);

  const openEditModal = (member) => {
    setSelectedMember(member);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedMember(null);
    setIsEditModalOpen(false);
  };

  const openUploadModal = (member) => {
    setSelectedMember(member);
    setSelectedFile(null);
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setSelectedMember(null);
    setSelectedFile(null);
    setSelectedPreview("");
    setIsUploadModalOpen(false);
  };

  const handleDeleteTeamMember = async (member) => {
    if (!member?.id) return;

    const ok = window.confirm(
      `Êtes-vous sûr de vouloir supprimer ${member.nom} ${member.prenom} ?`
    );
    if (!ok) return;

    try {
      await deleteTeamMember(member.id);
      toast.success("Membre supprimé avec succès");
      fetchTeamMembers();
    } catch (error) {
      console.error("Erreur suppression team:", error);
      toast.error("Erreur lors de la suppression du membre.");
    }
  };

  const persistOrder = async (updatedTeam) => {
    setTeam(updatedTeam);
    setIsReordering(true);

    try {
      await updateTeamOrder(updatedTeam);
      await fetchTeamMembers();
      toast.success("Ordre mis à jour");
    } catch (e) {
      console.error("Erreur update order:", e);
      toast.error("Erreur lors de la mise à jour de l’ordre.");
      fetchTeamMembers();
    } finally {
      setIsReordering(false);
    }
  };

  const moveTeamMemberUp = async (index) => {
    if (isReordering || index === 0) return;

    const newTeam = [...team];
    [newTeam[index - 1], newTeam[index]] = [newTeam[index], newTeam[index - 1]];
    const normalized = normalizeMembers(newTeam);

    await persistOrder(normalized);
  };

  const moveTeamMemberDown = async (index) => {
    if (isReordering || index === team.length - 1) return;

    const newTeam = [...team];
    [newTeam[index + 1], newTeam[index]] = [newTeam[index], newTeam[index + 1]];
    const normalized = normalizeMembers(newTeam);

    await persistOrder(normalized);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files?.[0] ?? null);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!selectedMember?.id || !selectedFile) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      await updateTeamMemberPhoto(selectedMember.id, selectedFile, token);
      await fetchTeamMembers();
      toast.success("Photo mise à jour avec succès !");
      closeUploadModal();
    } catch (error) {
      console.error("Erreur upload photo:", error);
      toast.error("Erreur lors de l'upload de l'image.");
    } finally {
      setIsUploading(false);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setCategoryFilter("all");
  };

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-3 sm:px-6">
        <div className="rounded-3xl overflow-hidden shadow-sm border border-slate-200/70 dark:border-slate-800 bg-white/90 dark:bg-slate-900/50 backdrop-blur">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-white text-2xl sm:text-3xl font-extrabold tracking-tight">
                  Gestion des membres du Comité
                </h1>
                <p className="text-white/90 text-sm sm:text-[15px] mt-1 max-w-2xl">
                  Ajoutez, modifiez, filtrez et réorganisez facilement l’affichage
                  de votre équipe.
                </p>
              </div>

              <Button
                text="Ajouter un membre"
                icon={FaPlus}
                onClick={() => setIsCreateModalOpen(true)}
                className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-2xl gap-2"
                hoverLabel="Ajouter un nouveau membre"
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <StatCard
                icon={FaUsers}
                title="Membres"
                value={stats.total}
                sub="Total enregistré"
                accent="indigo"
              />
              <StatCard
                icon={FaSearch}
                title="Résultats"
                value={stats.visible}
                sub="Affichés avec filtres"
                accent="slate"
              />
              <StatCard
                icon={FaLayerGroup}
                title="Catégories"
                value={stats.categoriesCount}
                sub="Catégories distinctes"
                accent="emerald"
              />
              <StatCard
                icon={FaImage}
                title="Photos"
                value={stats.withPhoto}
                sub="Membres avec photo"
                accent="amber"
              />
            </div>

            {/* Filters */}
            <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/80 dark:bg-slate-950/40 p-4 shadow-sm">
              <div className="flex flex-col xl:flex-row xl:items-end gap-3">
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Recherche
                  </label>
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Nom, prénom, fonction, catégorie..."
                      className="w-full rounded-2xl border border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                  </div>
                </div>

                <div className="w-full xl:w-72 space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Catégorie
                  </label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  >
                    <option value="all">Toutes les catégories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900 transition"
                  >
                    <FaUndo className="h-3.5 w-3.5" />
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-3xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 flex items-center gap-4 bg-white/70 dark:bg-slate-900/40"
                  >
                    <div className="h-16 w-16 rounded-full bg-slate-200 dark:bg-slate-700" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-2/5 bg-slate-200 dark:bg-slate-700 rounded" />
                      <div className="h-3 w-1/3 bg-slate-200 dark:bg-slate-700 rounded" />
                      <div className="h-3 w-1/4 bg-slate-200 dark:bg-slate-700 rounded" />
                    </div>
                    <div className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                  </div>
                ))}
              </div>
            )}

            {!loading && team.length === 0 && (
              <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 text-center py-14 px-6">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-90" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun membre pour le moment
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Clique sur “Ajouter un membre” pour commencer.
                </p>
              </div>
            )}

            {!loading && team.length > 0 && filteredTeam.length === 0 && (
              <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 text-center py-14 px-6">
                <div className="mx-auto h-16 w-16 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center">
                  <FaSearch className="text-slate-500 dark:text-slate-300" />
                </div>
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun résultat
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Essaie de modifier la recherche ou le filtre catégorie.
                </p>
              </div>
            )}

            {!loading && filteredTeam.length > 0 && (
              <>
                {/* Desktop table */}
                <div className="hidden lg:block">
                  <div className="overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/30 shadow-sm">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/90 dark:bg-slate-900/80 uppercase tracking-wide text-[11px]">
                          {[
                            "Membre",
                            "Fonction",
                            "Catégorie",
                            "Position",
                            "Actions",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-bold text-slate-600 dark:text-slate-300"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {filteredTeam.map((member) => {
                          const realIndex = team.findIndex((m) => m.id === member.id);
                          const fonctionText = stripHtml(member.fonction);

                          return (
                            <tr
                              key={member.id}
                              className="group transition-all duration-150 hover:bg-slate-50/80 dark:hover:bg-slate-800/30"
                            >
                              <td className="px-4 py-4 align-middle">
                                <div className="flex items-center gap-3 min-w-[280px]">
                                  <img
                                      src={toAbsolute(member.photoURL) || defaultAvatar}
                                      alt={`${member.nom} ${member.prenom}`}
                                      onError={(e) => {
                                        e.currentTarget.src = defaultAvatar;
                                      }}
                                      className="h-14 w-14 rounded-full object-cover shadow ring-2 ring-white dark:ring-slate-800"
                                    />
                                  

                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                      {member.prenom} {member.nom}
                                    </div>
                                    {/* <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                      {member.photoURL ? "Photo renseignée" : "Photo par défaut"}
                                    </div> */}
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                                {member.fonction ? (
                                  <div
                                    className="max-w-[360px] prose prose-sm dark:prose-invert prose-p:my-0 prose-strong:text-inherit"
                                    dangerouslySetInnerHTML={{ __html: member.fonction }}
                                  />
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                                {/* {fonctionText && (
                                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    {truncate(fonctionText, 80)}
                                  </div>
                                )} */}
                              </td>

                              <td className="px-4 py-4 text-sm">
                                {member.categorie ? (
                                  <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200 ring-1 ring-indigo-100 dark:ring-indigo-900/40">
                                    {member.categorie}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>

                              <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                                <div className="inline-flex items-center gap-2">
                                  <span className="inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-xl border border-slate-200 dark:border-slate-700 px-2 text-sm font-extrabold text-slate-800 dark:text-slate-100 bg-white/80 dark:bg-slate-900/50">
                                    {member.position}
                                  </span>

                                  <div className="flex flex-col">
                                    <button
                                      onClick={() => moveTeamMemberUp(realIndex)}
                                      disabled={isReordering || realIndex === 0}
                                      className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition disabled:opacity-30"
                                      title="Monter"
                                    >
                                      <TbTriangleFilled />
                                    </button>
                                    <button
                                      onClick={() => moveTeamMemberDown(realIndex)}
                                      disabled={
                                        isReordering || realIndex === team.length - 1
                                      }
                                      className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition disabled:opacity-30"
                                      title="Descendre"
                                    >
                                      <TbTriangleInvertedFilled />
                                    </button>
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  {/* <button
                                    onClick={() => openUploadModal(member)}
                                    className="inline-flex items-center justify-center h-9 w-9 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                                    title="Changer la photo"
                                  >
                                    <FaCamera className="h-4 w-4" />
                                  </button> */}
                                  <ButtonEdit onClick={() => openEditModal(member)} />
                                  <ButtonDelete
                                    onClick={() => handleDeleteTeamMember(member)}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile / Tablet cards */}
                <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredTeam.map((member) => {
                    const realIndex = team.findIndex((m) => m.id === member.id);

                    return (
                      <div
                        key={`card-${member.id}`}
                        className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/30 p-4 shadow-sm transition hover:shadow-md"
                      >
                        <div className="flex items-start gap-4">
                          <img
                              src={toAbsolute(member.photoURL) || defaultAvatar}
                              alt={`${member.nom} ${member.prenom}`}
                              onError={(e) => {
                                e.currentTarget.src = defaultAvatar;
                              }}
                              className="h-16 w-16 rounded-full object-cover shadow ring-2 ring-white dark:ring-slate-800"
                            />
                          

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate">
                                  {member.prenom} {member.nom}
                                </h3>

                                <div className="mt-1">
                                  {member.categorie ? (
                                    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200 ring-1 ring-indigo-100 dark:ring-indigo-900/40">
                                      {member.categorie}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-slate-400">Sans catégorie</span>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-1">
                                <span className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-xl border border-slate-200 dark:border-slate-700 px-2 text-sm font-extrabold text-slate-800 dark:text-slate-100 bg-white/80 dark:bg-slate-900/50">
                                  {member.position}
                                </span>
                                <div className="flex flex-col">
                                  <button
                                    onClick={() => moveTeamMemberUp(realIndex)}
                                    disabled={isReordering || realIndex === 0}
                                    className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition disabled:opacity-30"
                                    title="Monter"
                                  >
                                    <TbTriangleFilled />
                                  </button>
                                  <button
                                    onClick={() => moveTeamMemberDown(realIndex)}
                                    disabled={
                                      isReordering || realIndex === team.length - 1
                                    }
                                    className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition disabled:opacity-30"
                                    title="Descendre"
                                  >
                                    <TbTriangleInvertedFilled />
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div className="mt-3 text-sm text-slate-700 dark:text-slate-300">
                              {member.fonction ? (
                                <div
                                  className="prose prose-sm dark:prose-invert prose-p:my-0 prose-strong:text-inherit"
                                  dangerouslySetInnerHTML={{ __html: member.fonction }}
                                />
                              ) : (
                                "—"
                              )}
                            </div>

                            <div className="mt-4 flex items-center gap-2 flex-wrap">
                              {/* <button
                                onClick={() => openUploadModal(member)}
                                className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                              >
                                <FaCamera className="h-3.5 w-3.5" />
                                Photo
                              </button> */}
                              <ButtonEdit onClick={() => openEditModal(member)} />
                              <ButtonDelete
                                onClick={() => handleDeleteTeamMember(member)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <CreateTeamMemberModal
          isActive={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refreshTeam={fetchTeamMembers}
        />
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <EditTeamMemberModal
          isActive={isEditModalOpen}
          onClose={closeEditModal}
          refreshTeam={fetchTeamMembers}
          memberData={selectedMember}
        />
      )}

      {/* Upload Photo Modal */}
      {isUploadModalOpen && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={closeUploadModal}
          />
          <div className="relative w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden border border-slate-200/60 dark:border-slate-800">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
              <h2 className="text-white text-lg font-bold">
                Changer la photo — {selectedMember.nom} {selectedMember.prenom}
              </h2>
            </div>

            <div className="bg-white dark:bg-slate-900 p-6">
              <form onSubmit={handleUploadSubmit} className="space-y-5">
                <div className="flex items-center gap-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4">
                  <img
                    src={toAbsolute(selectedMember.photoURL) || defaultAvatar}
                    alt="Actuelle"
                    onError={(e) => {
                      e.currentTarget.src = defaultAvatar;
                    }}
                    className="h-16 w-16 rounded-full object-cover shadow ring-2 ring-slate-200 dark:ring-slate-700"
                  />
                  <div className="text-sm text-slate-600 dark:text-slate-300">
                    <p className="font-semibold text-slate-900 dark:text-white">
                      Photo actuelle
                    </p>
                    <p className="text-xs opacity-80 mt-1">
                      Formats acceptés : JPG, PNG, WebP
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                    Nouvelle image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="block w-full text-sm file:mr-4 file:rounded-2xl file:border-0 file:bg-slate-100 file:px-4 file:py-2.5 file:font-semibold file:text-slate-800 hover:file:bg-slate-200 dark:file:bg-slate-800 dark:file:text-slate-100 dark:hover:file:bg-slate-700"
                  />
                </div>

                {selectedFile && selectedPreview && (
                  <div className="flex items-center gap-4 rounded-2xl border border-violet-200 dark:border-violet-900/40 bg-violet-50 dark:bg-violet-950/20 p-4">
                    <img
                      src={selectedPreview}
                      alt="Aperçu"
                      className="h-16 w-16 rounded-full object-cover ring-2 ring-violet-400/70"
                    />
                    <div className="text-xs text-slate-600 dark:text-slate-300">
                      <div className="font-semibold text-slate-900 dark:text-white">
                        Aperçu sélectionné
                      </div>
                      <div className="mt-1">
                        {selectedFile.name} — {(selectedFile.size / 1024).toFixed(0)} Ko
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    text="Fermer"
                    onClick={closeUploadModal}
                    icon={FaTimes}
                    className="gap-2 !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-2xl"
                    type="button"
                  />
                  <Button
                    text={isUploading ? "Envoi..." : "Sauvegarder"}
                    type="submit"
                    icon={FaSave}
                    className="gap-2 !rounded-2xl"
                    disabled={isUploading}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </section>
  );
};

function StatCard({ icon: Icon, title, value, sub, accent = "slate" }) {
  const accents = {
    indigo:
      "from-indigo-500/10 to-violet-500/10 border-indigo-200/60 dark:border-indigo-900/40",
    emerald:
      "from-emerald-500/10 to-green-500/10 border-emerald-200/60 dark:border-emerald-900/40",
    amber:
      "from-amber-500/10 to-orange-500/10 border-amber-200/60 dark:border-amber-900/40",
    slate:
      "from-slate-500/5 to-slate-500/10 border-slate-200/70 dark:border-slate-800",
  };

  return (
    <div
      className={cn(
        "rounded-3xl border bg-gradient-to-br p-4 shadow-sm",
        "bg-white/90 dark:bg-slate-900/50 backdrop-blur",
        accents[accent] || accents.slate
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
            {title}
          </div>
          <div className="mt-2 text-3xl font-extrabold text-slate-900 dark:text-white">
            {value}
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {sub}
          </div>
        </div>

        <div className="h-11 w-11 rounded-2xl bg-white/80 dark:bg-slate-950/60 border border-slate-200/70 dark:border-slate-800 grid place-items-center text-slate-700 dark:text-slate-200">
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

export default TeamManagement;